// Live store listings, from @verso/shared's zero-dependency `store-urls` leaf.
// The DEEP import (not the barrel) is deliberate: the barrel pulls zod + the
// full constant corpus and would bloat this lightweight landing page.
import { APP_STORE_URL, PLAY_STORE_URL } from '@verso/shared/src/constants/store-urls'
import { trackWeb, WEB_EVENTS } from '../lib/analytics'
import { trackGoogleDownload } from '../lib/google-ads'
import { newEventId, trackMetaStoreClick } from '../lib/meta-pixel'
import { usePlatform } from '../lib/platform'

// The first five are the aspirational landing (`/`) plus the shared sticky bar.
// The `get_*` members are the conversion page (`/get`), kept as their own
// namespace so PostHog can segment one page's clicks from the other's when the
// two are A/B compared by pointing different Meta ad sets at different URLs.
export type DownloadCtaPlacement =
  | 'hero'
  | 'sticky'
  | 'meet'
  | 'payin3'
  | 'wedge'
  | 'get_hero'
  | 'get_steps'
  | 'get_price'
  | 'get_faq'
  | 'get_final'

interface DownloadCtaProps {
  placement: DownloadCtaPlacement
  /** Extra class applied to EACH store button (e.g. the sticky bar's compact form). */
  className?: string
}

type ButtonStore = 'ios' | 'android'

const IOS_PATH =
  'M17.05 12.04c-.02-2.13 1.74-3.16 1.82-3.21-.99-1.45-2.54-1.65-3.09-1.67-1.32-.13-2.57.77-3.24.77-.67 0-1.7-.75-2.79-.73-1.44.02-2.76.83-3.5 2.11-1.49 2.58-.38 6.41 1.07 8.51.71 1.03 1.55 2.19 2.65 2.15 1.06-.04 1.46-.69 2.74-.69 1.28 0 1.64.69 2.77.66 1.14-.02 1.86-1.05 2.56-2.09.81-1.2 1.14-2.36 1.16-2.42-.03-.01-2.22-.85-2.25-3.39zM14.93 5.78c.58-.71.98-1.69.87-2.66-.84.03-1.86.56-2.46 1.26-.54.61-1.02 1.6-.89 2.55.94.07 1.9-.48 2.48-1.15z'

const ANDROID_PATH =
  'M3.6 2.5c-.4.2-.6.7-.6 1.3v16.4c0 .6.2 1.1.6 1.3l9.3-9-9.3-10zm10.6 10.2l2.3 2.3-9.5 5.5 7.2-7.8zm-7.2-9.4l9.5 5.5-2.3 2.3-7.2-7.8zm9.5 9l3.4-1.9c.6-.4.6-1.2 0-1.6l-3.4-1.9-2.5 2.7 2.5 2.7z'

const BUTTONS: Record<ButtonStore, { href: string; lead: string; name: string; path: string }> = {
  ios: { href: APP_STORE_URL, lead: 'Download on the', name: 'App Store', path: IOS_PATH },
  android: { href: PLAY_STORE_URL, lead: 'Get it on', name: 'Google Play', path: ANDROID_PATH },
}

const DESKTOP_ORDER: ButtonStore[] = ['ios', 'android']

/**
 * THE download button. Shows only the visitor's own store on mobile and both
 * badges on desktop.
 *
 * Deliberately NO `target="_blank"` / `rel`: opening a new tab breaks the store
 * hand-off inside the Instagram + Facebook webviews that most of this page's
 * paid traffic arrives in, which is the single biggest conversion leak here.
 */
export function DownloadCta({ placement, className }: DownloadCtaProps) {
  const { store, context } = usePlatform()
  const stores: ButtonStore[] = store === null ? DESKTOP_ORDER : [store]

  const onDownloadClick = (buttonStore: ButtonStore) => {
    // One id, three sinks: PostHog for the funnel, Meta and Google Ads for ad
    // optimisation. It is also the dedup key - Meta's eventID against a future
    // server-side Conversions API event, Google's transaction_id against a
    // retried conversion ping. `context` stays PostHog-only, as it always has.
    const eventId = newEventId()
    trackWeb(WEB_EVENTS.WEB_DOWNLOAD_CTA_CLICKED, {
      store: buttonStore,
      placement,
      context,
      event_id: eventId,
    })
    trackMetaStoreClick({ store: buttonStore, placement }, eventId)
    trackGoogleDownload({ store: buttonStore, placement }, eventId)
  }

  return (
    <>
      {stores.map((key) => {
        const button = BUTTONS[key]
        return (
          <a
            key={key}
            className={className ? `storebtn ${className}` : 'storebtn'}
            href={button.href}
            onClick={() => onDownloadClick(key)}
          >
            <svg className='logo' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
              <path d={button.path} />
            </svg>
            <span className='txt'>
              <small>{button.lead}</small>
              <strong>{button.name}</strong>
            </span>
          </a>
        )
      })}
    </>
  )
}
